import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useNotifyWhenEmpty } from '../hooks/useNotifyWhenEmpty'
import { useTeamStaleGroups } from '../hooks/useTeamStaleGroups'
import { useTeamStaleIncidentIds } from '../hooks/useTeamStaleIncidentIds'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { useLightMode } from '@/hooks/useLightMode'
import { useOpenIncident } from '@/hooks/useOpenIncident'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { useTranslation } from 'react-i18next'

import {
  ActionBar,
  Alert,
  Button,
  Box as DarkBox,
  DarkProvider,
  Text as DarkText,
  Modal,
  ResolveIcon,
} from '@hivewatch/dark'

import { StaleResolveScope } from '@/graphql/generated/schemas'
import { trackEvent } from '@/utils/analytics'
import { tVar } from '@/utils/i18n'

import {
  SELECTION_BAR_Z_INDEX,
  TEAM_STALE_WORK_ANALYTICS,
} from '../constants/constants'
import type { TeamStaleIncident } from '../types/types'
import { getTeamGroupKey } from '../utils/utils'
import { StaleBulkResolveModal } from './StaleBulkResolveModal'
import { TeamStaleGroupSection } from './TeamStaleGroupSection'
import { TeamStaleGroupsSkeleton } from './TeamStaleGroupsSkeleton'

interface TeamStaleWorkModalIProps {
  isOpen: boolean
  onClose: () => void
  /** Called once when the open modal's list settles at zero incidents. */
  onAllResolved?: () => void
  /** Called after a successful bulk resolve, so the banner count refreshes. */
  onResolved?: () => void
  /**
   * Resolved by the time the modal can open: the banner that opens it only
   * renders once the org name is available (ENG-32179).
   */
  orgName?: string
}

/** What the operator asked to resolve; snapshot at button-click time. */
interface ConfirmStateIProps {
  incidentIds: string[]
  scope: StaleResolveScope
}

/**
 * Lists the whole team's stale incidents grouped under expandable per-user
 * sections (owner first, guard as fallback), worst backlog first. Groups
 * come from the server-side aggregation (ENG-32213) — one cheap request per
 * page of groups, with infinite scroll for the rest — and each section
 * pages its incidents server-side via the attribution filter on first
 * expansion. Card clicks open the incident drawer WITHOUT closing this
 * modal. Resolved incidents drop out of expanded sections via the hooks'
 * render-time filters, and a section whose last active card resolves
 * reports itself emptied so its stale aggregate row is hidden; when no
 * visible groups remain, the all-done state shows.
 *
 * With the staleBulkResolve flag on (ENG-32272), operators/PMs/implementers
 * get card checkboxes + per-group select-all over the loaded incidents, and
 * any selection surfaces the same floating ActionBar the incidents table
 * uses; PMs/implementers additionally get the program-wide resolve button,
 * which gathers the org-wide id list at click time (the mutation requires
 * explicit ids). Every mode confirms through StaleBulkResolveModal.
 */
export const TeamStaleWorkModal = ({
  isOpen,
  onClose,
  onAllResolved,
  onResolved,
  orgName,
}: TeamStaleWorkModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const isStaleBulkResolveEnabled = useFeatureFlag(FeatureFlag.staleBulkResolve)
  const { canBulkAction, isPM, isImplementer } = usePermissions()
  const { showError } = useToasts({ colorScheme, isRebrand: true })
  const {
    groups,
    cutoff,
    hasMore,
    loadMore,
    isLoading,
    isLoadingMore,
    isError,
    refetch,
  } = useTeamStaleGroups({ isOpen })
  const { fetchAllStaleIncidentIds, isFetchingIds } = useTeamStaleIncidentIds()
  const { openIncident } = useOpenIncident()

  // Group-level infinite scroll: the sentinel sits at the bottom of the
  // modal's scrollable body and pages in the next batch of groups.
  const { loadMoreRef } = useInfiniteScroll({
    hasNextPage: hasMore,
    isLoading: isLoading || isLoadingMore,
    onLoadMore: loadMore,
  })

  // SELECTED needs operator/PM/implementer; PROGRAM needs PM or
  // implementer (also enforced server-side).
  const canBulkResolve = isStaleBulkResolveEnabled && canBulkAction
  const canProgramResolve = isStaleBulkResolveEnabled && (isPM || isImplementer)

  // Sections start collapsed on every open; multiple can be open at once.
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  )
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [confirmState, setConfirmState] = useState<ConfirmStateIProps | null>(
    null
  )
  // Groups whose last active card was resolved through the drawer: the
  // server aggregate row never reacts to normalized status updates, so the
  // section reports emptiness and the modal hides the row — otherwise a
  // stale header/count lingers and the all-done state below is unreachable
  // until an explicit refetch or reopen.
  const [emptiedGroupKeys, setEmptiedGroupKeys] = useState<Set<string>>(
    new Set()
  )
  useEffect(() => {
    if (!isOpen) {
      setExpandedGroups({})
      setSelectedIds(new Set())
      setConfirmState(null)
      setEmptiedGroupKeys(new Set())
    }
  }, [isOpen])

  const handleGroupEmptied = useCallback((groupKey: string) => {
    // Sections re-report while their emptied condition holds; returning the
    // same Set skips the no-op re-render.
    setEmptiedGroupKeys((prev) => {
      if (prev.has(groupKey)) return prev
      return new Set(prev).add(groupKey)
    })
  }, [])

  const visibleGroups = useMemo(
    () =>
      groups.filter((group) => !emptiedGroupKeys.has(getTeamGroupKey(group))),
    [emptiedGroupKeys, groups]
  )

  // Incidents can leave the list without a bulk resolve (e.g. resolved
  // individually through the drawer, which opens over this modal); the
  // modal no longer holds any incident data (server paging, ENG-32213), so
  // each section prunes its own drawer-resolved ids through this callback
  // and the ActionBar never counts rows that are no longer shown.
  const handleSelectionPruned = useCallback((incidentIds: string[]) => {
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => !incidentIds.includes(id)))
      return next.size === prev.size ? prev : next
    })
  }, [])

  useNotifyWhenEmpty({
    count: visibleGroups.length,
    isError,
    // hasMore counts as unsettled: with every loaded group emptied but more
    // pages server-side, the list is not "all done" yet — the sentinel will
    // page them in and the emptiness re-evaluates then.
    isLoading: isLoading || hasMore,
    isOpen,
    onEmpty: onAllResolved,
  })

  const handleCardClick = (incident: TeamStaleIncident) => {
    trackEvent(TEAM_STALE_WORK_ANALYTICS.cardClick, {
      incident_id: incident.id,
    })
    openIncident({ displayId: incident.displayId, incidentId: incident.id })
  }

  const handleToggleGroup = (groupKey: string, staleCount: number) => {
    // Tracked outside the state updater: updaters must stay pure, and
    // StrictMode double-invokes them in dev, which would double-fire the
    // event per click.
    const isExpanding = !expandedGroups[groupKey]
    if (isExpanding) {
      trackEvent(TEAM_STALE_WORK_ANALYTICS.groupExpand, {
        stale_count: staleCount,
        user_id: groupKey,
      })
    }
    setExpandedGroups((prev) => ({ ...prev, [groupKey]: isExpanding }))
  }

  const handleToggleSelect = (incidentId: string, isSelected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (isSelected) {
        next.add(incidentId)
      } else {
        next.delete(incidentId)
      }
      return next
    })
  }

  const handleSelectAll = (incidentIds: string[], isSelected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      for (const id of incidentIds) {
        if (isSelected) {
          next.add(id)
        } else {
          next.delete(id)
        }
      }
      return next
    })
  }

  const handleResolveSelectedClick = () => {
    trackEvent(TEAM_STALE_WORK_ANALYTICS.resolveSelectedClick, {
      incident_count: selectedIds.size,
    })
    setConfirmState({
      incidentIds: [...selectedIds],
      scope: StaleResolveScope.Selected,
    })
  }

  const handleResolveProgramClick = async () => {
    // The mutation requires explicit ids for every scope, and the modal no
    // longer loads every incident (ENG-32213) — gather the org-wide id list
    // now, at the same cutoff as the displayed groups.
    try {
      const incidentIds = await fetchAllStaleIncidentIds(cutoff)
      if (incidentIds.length === 0) {
        // Everything resolved since the groups loaded; refresh the list to
        // the all-done state instead of confirming a resolve of nothing
        // (the backend rejects an empty id list).
        refetch()
        return
      }
      trackEvent(TEAM_STALE_WORK_ANALYTICS.resolveProgramClick, {
        incident_count: incidentIds.length,
      })
      setConfirmState({ incidentIds, scope: StaleResolveScope.Program })
    } catch {
      // Already reported to Sentry by useTeamStaleIncidentIds.
      showError(t('incidents:stale_bulk_resolve_error'))
    }
  }

  const handleResolved = () => {
    // Resolved incidents leave the stale set server-side; the group
    // aggregates and the banner count both re-derive from a fresh fetch.
    setSelectedIds(new Set())
    refetch()
    onResolved?.()
  }

  return (
    <>
      <Modal
        // The grouped list scrolls and the modal floats centered instead of
        // stretching against the banner.
        bodyStyleOverrides={{ maxH: '60vh', overflowY: 'auto' }}
        colorScheme={colorScheme}
        isOpen={isOpen}
        isSaveHidden={true}
        maxW='560px'
        onClose={onClose}
        size='compact'
        title={tVar(t, 'incidents:stale_team_modal_title', {
          orgName: orgName ?? '',
        })}
      >
        <DarkBox display='flex' flexDirection='column' gap='12px'>
          <DarkText fontSize='14px'>
            {t('incidents:stale_team_modal_subtitle')}
          </DarkText>
          {!isLoading &&
          !isError &&
          visibleGroups.length > 0 &&
          canProgramResolve ? (
            <DarkBox alignItems='center' display='flex' gap='8px'>
              <Button
                colorScheme={colorScheme}
                data-testid='team-stale-resolve-program'
                isLoading={isFetchingIds}
                onClick={handleResolveProgramClick}
                size='sm'
                variant='secondary'
              >
                {t('incidents:stale_bulk_resolve_all_program')}
              </Button>
            </DarkBox>
          ) : null}
          {isLoading ? <TeamStaleGroupsSkeleton /> : null}
          {isError ? (
            <>
              <Alert
                colorScheme={colorScheme}
                title={t('common:error')}
                variant='error'
              />
              <Button
                colorScheme={colorScheme}
                onClick={() => refetch()}
                variant='secondary'
              >
                {t('common:retry')}
              </Button>
            </>
          ) : null}
          {/* !hasMore: with every loaded group emptied but more pages
              server-side, "all done" would sit above a sentinel still
              paging groups in. */}
          {!isLoading && !isError && !hasMore && visibleGroups.length === 0 ? (
            <DarkBox data-testid='team-stale-work-all-done'>
              <Alert
                colorScheme={colorScheme}
                title={tVar(t, 'incidents:stale_team_all_done', {
                  orgName: orgName ?? '',
                })}
                variant='success'
              />
            </DarkBox>
          ) : null}
          {isLoading
            ? null
            : visibleGroups.map((group) => {
                const groupKey = getTeamGroupKey(group)
                return (
                  <TeamStaleGroupSection
                    canBulkResolve={canBulkResolve}
                    cutoff={cutoff}
                    group={group}
                    isExpanded={Boolean(expandedGroups[groupKey])}
                    key={groupKey}
                    onCardClick={handleCardClick}
                    onEmptied={() => handleGroupEmptied(groupKey)}
                    onSelectAll={handleSelectAll}
                    onSelectionPruned={handleSelectionPruned}
                    onToggle={() =>
                      handleToggleGroup(groupKey, group.incidentCount)
                    }
                    onToggleSelect={handleToggleSelect}
                    selectedIncidentIds={selectedIds}
                  />
                )
              })}
          {/* Scroll sentinel for the next page of groups; a failed page is
              reported by the hook and the sentinel stays for another try. */}
          {!isLoading && !isError && hasMore ? (
            <DarkBox data-testid='team-stale-groups-sentinel' ref={loadMoreRef}>
              {isLoadingMore ? <TeamStaleGroupsSkeleton /> : null}
            </DarkBox>
          ) : null}
        </DarkBox>
      </Modal>
      {/* Same floating selection bar as the incidents table: checking any
          box surfaces it with the count and the Bulk Resolve action. It is a
          SIBLING of the Modal because the modal wrapper's drop-shadow filter
          would hijack position:fixed and pin the bar inside the dialog;
          standalone dark components also need their own DarkProvider. Hidden
          while the confirm modal is up so it never floats over it. */}
      {isOpen && canBulkResolve && selectedIds.size > 0 && !confirmState ? (
        <DarkProvider>
          <DarkBox
            bottom='24px'
            data-testid='team-stale-selection-bar'
            left='50%'
            position='fixed'
            transform='translateX(-50%)'
            // Above the modal overlay so the bar is clickable.
            zIndex={SELECTION_BAR_Z_INDEX}
          >
            <ActionBar
              actions={[
                {
                  icon: <ResolveIcon color='currentColor' size={16} />,
                  label: t('incidents:bulk_resolve'),
                  onClick: handleResolveSelectedClick,
                },
              ]}
              colorScheme={colorScheme}
              onClearSelection={() => setSelectedIds(new Set())}
              selectionLabel={`${selectedIds.size} ${
                selectedIds.size === 1
                  ? t('incidents:table_incident_selected')
                  : t('incidents:table_incidents_selected')
              }`}
            />
          </DarkBox>
        </DarkProvider>
      ) : null}
      <StaleBulkResolveModal
        incidentIds={confirmState?.incidentIds ?? []}
        isOpen={Boolean(confirmState)}
        onClose={() => setConfirmState(null)}
        onResolved={handleResolved}
        orgName={orgName}
        scope={confirmState?.scope ?? StaleResolveScope.Selected}
      />
    </>
  )
}
